/**
 * Auth context object + state shape. Lives in its own file so the provider
 * component file (`AuthContext.tsx`) only exports React components, which
 * keeps Vite's React Fast Refresh happy.
 */
import { createContext } from 'react';
import type { User } from 'firebase/auth';
import type { UserProfile } from '@/src/modules/services/profile.service';

export interface AuthState {
  user: User | null;
  /**
   * Local DB profile (name/avatar/role/plan/updatedAt). Source of truth for
   * everything the UI shows — Firebase's User object is only used for
   * identity and is not refreshed when the admin updates their profile.
   */
  profile: UserProfile | null;
  isAdmin: boolean;
  loading: boolean;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  error: string | null;
  /**
   * Replace the in-memory profile. Call after a successful profile update so
   * every consumer of `useAuth().profile` (navbar avatar, dropdown, etc.)
   * reflects the new values without a page reload.
   */
  setProfile: (next: UserProfile | null) => void;
  /** Re-fetch the profile from `/v1/auth/me`. Used as a safety net. */
  refreshProfile: () => Promise<void>;
}

export const AuthContext = createContext<AuthState | null>(null);
